import { useCallback, useEffect } from 'react';
import { trackEvent, trackInteraction, trackFormSubmission, trackPageExit, trackScrollDepth } from '../utils/tagManager';
import useExitTracking from './useExitTracking';
import useScrollTracking from './useScrollTracking';

/**
 * Custom hook for analytics tracking in React components
 * Provides easy-to-use functions for tracking various user interactions
 * @param {Object} options - Options de configuration
 * @param {boolean} options.enableExitTracking - Activer le suivi des sorties de page (défaut: true)
 * @param {boolean} options.enableScrollTracking - Activer le suivi de défilement (défaut: true)
 * @param {boolean} options.enableElementTracking - Activer le suivi des éléments importants (défaut: true)
 */
const useAnalytics = (options = {}) => {
  const {
    enableExitTracking = true,
    enableScrollTracking = true,
    enableElementTracking = true,
    exitTrackingOptions = {},
    scrollTrackingOptions = {}
  } = options;

  // Utiliser les hooks de manière non conditionnelle
  const exitTrackingResult = useExitTracking(exitTrackingOptions);
  const scrollTrackingResult = useScrollTracking(scrollTrackingOptions);
  
  // Ajouter une fonction trackPageView pour éviter l'erreur "n is not a function"
  const trackPageView = useCallback((path) => {
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'page_view',
        page_path: path
      });
    }
  }, []);
  
  // Désactiver les fonctionnalités si nécessaire
  useEffect(() => {
    if (!enableExitTracking && exitTrackingResult && typeof exitTrackingResult.disable === 'function') {
      exitTrackingResult.disable();
    }
    
    if (!enableScrollTracking && scrollTrackingResult && typeof scrollTrackingResult.disable === 'function') {
      scrollTrackingResult.disable();
    }
  }, [enableExitTracking, enableScrollTracking, exitTrackingResult, scrollTrackingResult]);

  // Activer le suivi des éléments importants
  useEffect(() => {
    if (!enableElementTracking) return;

    // Sélecteurs pour les éléments importants à suivre
    const importantSelectors = [
      'button.cta', 
      'a.cta', 
      'button.primary', 
      'a.primary',
      '[data-track="true"]',
      '.nav-link',
      '.logo',
      '.footer-link',
      '.social-link',
      '.service-card',
      '.project-card',
      '.testimonial-card'
    ];

    // Fonction pour suivre les clics sur les éléments importants
    const handleImportantClick = (e) => {
      const element = e.currentTarget;
      const elementType = element.tagName.toLowerCase();
      const elementText = element.innerText || element.getAttribute('aria-label') || 'Sans texte';
      const elementId = element.id || '';
      const elementClass = Array.from(element.classList).join(' ');
      const elementSection = findParentSection(element);

      trackEvent('important_element_click', {
        element_type: elementType,
        element_text: elementText.substring(0, 50), // Limiter la longueur
        element_id: elementId,
        element_class: elementClass,
        element_section: elementSection,
        page_path: window.location.pathname,
        page_title: document.title
      });
    };

    // Fonction pour trouver la section parente
    const findParentSection = (element) => {
      let current = element;
      while (current && current !== document.body) {
        if (
          current.tagName === 'SECTION' || 
          current.hasAttribute('data-section-id') ||
          current.classList.contains('section')
        ) {
          return current.id || 
                 current.getAttribute('data-section-id') || 
                 Array.from(current.classList).join(' ');
        }
        current = current.parentElement;
      }
      return 'unknown';
    };

    // Ajouter des écouteurs d'événements pour les éléments importants
    const importantElements = document.querySelectorAll(importantSelectors.join(', '));
    importantElements.forEach(element => {
      element.addEventListener('click', handleImportantClick);
    });

    // Nettoyage lors du démontage du composant
    return () => {
      importantElements.forEach(element => {
        element.removeEventListener('click', handleImportantClick);
      });
    };
  }, [enableElementTracking]);

  // Track button clicks
  const trackButtonClick = useCallback((buttonName, section) => {
    trackInteraction('button', 'click', `${section} - ${buttonName}`);
  }, []);

  // Track form submissions
  const trackForm = useCallback((formName, success = true) => {
    trackFormSubmission(formName, success ? 'success' : 'failure');
  }, []);

  // Track custom events
  const trackCustomEvent = useCallback((eventName, eventParams = {}) => {
    trackEvent(eventName, eventParams);
  }, []);

  // Track link clicks
  const trackLinkClick = useCallback((linkName, url, isExternal = false) => {
    trackInteraction('link', 'click', linkName);
    
    if (isExternal) {
      trackEvent('outbound_link_click', {
        link_name: linkName,
        destination_url: url,
        category: 'Outbound'
      });
    }
  }, []);

  // Track service selection
  const trackServiceSelection = useCallback((serviceName) => {
    trackEvent('service_selection', {
      service_name: serviceName,
      category: 'Services'
    });
  }, []);

  // Track portfolio item views
  const trackPortfolioView = useCallback((projectName) => {
    trackEvent('portfolio_view', {
      project_name: projectName,
      category: 'Portfolio'
    });
  }, []);

  // Track user engagement time
  const trackEngagementTime = useCallback((seconds) => {
    trackEvent('engagement_time', {
      duration_seconds: seconds,
      page_path: window.location.pathname,
      page_title: document.title
    });
  }, []);

  // Track content visibility
  const trackContentVisibility = useCallback((contentId, isVisible, visiblePercentage) => {
    trackEvent('content_visibility', {
      content_id: contentId,
      is_visible: isVisible,
      visible_percentage: visiblePercentage,
      page_path: window.location.pathname
    });
  }, []);

  // Fonction pour suivre les interactions avec les éléments
  const trackElementInteraction = useCallback((element, action, data = {}) => {
    trackInteraction(element, action, data);
  }, []);

  return {
    trackPageView,
    trackCustomEvent,
    trackElementInteraction,
    trackForm,
    trackEngagementTime,
    trackContentVisibility,
    trackPortfolioView,
    trackButtonClick,
    trackLinkClick,
    trackServiceSelection
  };
};

export default useAnalytics;